import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Folder, Save, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Dropdown from '@/components/ui/Dropdown';
import { cn } from '@/lib/utils';
import { slideUp, fadeIn } from '@/lib/animations';
import useAuth from '../hooks/useAuth';
import apiService from '../services/api';
import { 
  ProjectCreateData, 
  DesiredTimeframe, 
  TargetMarket, 
  ToneNew, 
  FormatType 
} from '../types/project';

interface ContentType {
  id: string;
  name: string;
  description: string;
  icon?: string;
}

const ProjectCreate: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [formData, setFormData] = useState<ProjectCreateData>({
    name: '',
    description: '',
    content_type_id: '',
    tone_new: ToneNew.Inspirational,
    desired_timeframe: DesiredTimeframe.OneMinute,
    target_market: TargetMarket.Neutral,
    format_type: FormatType.ExplainerBurst
  });
  
  const [contentTypes, setContentTypes] = useState<ContentType[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingContentTypes, setLoadingContentTypes] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});

  // Fetch content types
  useEffect(() => {
    const fetchContentTypes = async () => {
      try {
        setLoadingContentTypes(true);
        const response = await apiService.getContentTypes();
        
        if (response.data?.status === 'success' && response.data.data?.contentTypes) {
          setContentTypes(response.data.data.contentTypes);
        } else {
          throw new Error('Failed to fetch content types');
        }
      } catch (error: any) {
        console.error('[CONTENT_TYPES] Error fetching:', error);
        setError('Failed to load content types');
      } finally {
        setLoadingContentTypes(false);
      }
    };

    fetchContentTypes();
  }, []);

  // Define dropdown options
  const timeframeOptions = [
    { value: DesiredTimeframe.ThirtySeconds, label: '30 seconds', description: 'Quick, punchy content perfect for social media' },
    { value: DesiredTimeframe.OneMinute, label: '1 minute', description: 'Short-form content with focused messaging' },
    { value: DesiredTimeframe.TwoMinutes, label: '2 minutes', description: 'Medium-length content for detailed explanations' },
    { value: DesiredTimeframe.FiveMinutes, label: '5 minutes', description: 'Longer-form content for comprehensive coverage' },
    { value: DesiredTimeframe.FifteenMinutes, label: '15 minutes', description: 'Extended content for in-depth analysis' },
    { value: DesiredTimeframe.More, label: 'More', description: 'Custom length or extended content' }
  ];

  const targetMarketOptions = [
    { value: TargetMarket.Progressive, label: 'Progressive', description: 'Forward-thinking, innovative, and open to new ideas' },
    { value: TargetMarket.Neutral, label: 'Neutral', description: 'Balanced, objective, and appeals to a broad audience' },
    { value: TargetMarket.Conservative, label: 'Conservative', description: 'Traditional values, established approaches, and proven methods' }
  ];

  const toneOptions = [
    { value: ToneNew.Poetic, label: 'Poetic', description: 'Artistic, lyrical, and emotionally evocative content' },
    { value: ToneNew.Inspirational, label: 'Inspirational', description: 'Motivating, uplifting, and empowering messaging' },
    { value: ToneNew.Reverent, label: 'Reverent', description: 'Respectful, dignified, and honorific tone' }
  ];

  const formatOptions = [
    { value: FormatType.BiographicMontage, label: 'Biographic Montage', description: 'Life story compilation with emotional narrative' },
    { value: FormatType.NewReport, label: 'News Report', description: 'Factual, journalistic style with current events focus' },
    { value: FormatType.ExplainerBurst, label: 'Explainer Burst', description: 'Quick, educational content that breaks down complex topics' },
    { value: FormatType.OpinionSnap, label: 'Opinion Snap', description: 'Personal viewpoint with persuasive elements' },
    { value: FormatType.Commercial, label: 'Commercial', description: 'Promotional content designed to drive action' },
    { value: FormatType.TeaserTrailer, label: 'Teaser Trailer', description: 'Preview content that builds anticipation' }
  ];

  const handleInputChange = (field: keyof ProjectCreateData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = (): boolean => {
    const errors: {[key: string]: string} = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Project name is required';
    }
    
    if (!formData.content_type_id) {
      errors.content_type_id = 'Content type is required';
    }
    
    if (!formData.tone_new) {
      errors.tone_new = 'Tone is required';
    }
    
    if (!formData.desired_timeframe) {
      errors.desired_timeframe = 'Desired timeframe is required';
    }
    
    if (!formData.target_market) {
      errors.target_market = 'Target market is required';
    }
    
    if (!formData.format_type) {
      errors.format_type = 'Format is required';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      console.log('[PROJECT_CREATE] Creating project:', formData);
      const response = await apiService.createProject(formData);
      
      if (response.data?.status === 'success' && response.data.data) {
        console.log('[PROJECT_CREATE] Project created successfully:', response.data.data);
        // Navigate to the new project detail page
        navigate(`/projects/${response.data.data.id}`);
      } else {
        throw new Error('Failed to create project');
      }
    } catch (error: any) {
      console.error('[PROJECT_CREATE] Error:', error);
      setError(error.message || 'Failed to create project');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/projects');
  };

  if (loadingContentTypes) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading content types...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container max-w-2xl mx-auto px-4 py-8">
        {/* Header */}
        <div className={cn(slideUp(), "mb-8")}>
          <Button
            variant="ghost"
            onClick={handleCancel}
            className="mb-4 -ml-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Projects
          </Button>
          
          <div className="flex items-center mb-4">
            <Folder className="h-6 w-6 text-primary mr-2" />
            <h1 className="text-3xl font-bold tracking-tight">Create New Project</h1>
          </div>
            <p className="text-lg text-muted-foreground">
              Set up your multi-video project with advanced configuration options.
            </p>
        </div>

        {/* Form */}
        <div className={cn(fadeIn(200), "bg-background rounded-xl shadow-sm border border-border p-6")}>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Project Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium mb-2">
                Project Name *
              </label>
              <input
                type="text"
                id="name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className={cn(
                  "w-full px-3 py-2 border rounded-md bg-background",
                  validationErrors.name ? "border-red-500" : "border-input"
                )}
                placeholder="Enter your project name"
                disabled={loading}
              />
              {validationErrors.name && (
                <p className="mt-1 text-sm text-red-500">{validationErrors.name}</p>
              )}
            </div>

            {/* Description */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium mb-2">
                Description
              </label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                className="w-full px-3 py-2 border border-input rounded-md bg-background"
                placeholder="Describe your project (optional)"
                rows={3}
                disabled={loading}
              />
            </div>

            {/* Content Type */}
            <Dropdown
              label="Content Type"
              value={formData.content_type_id}
              options={contentTypes.map(type => ({
                value: type.id,
                label: type.name,
                description: type.description
              }))}
              onChange={(value) => handleInputChange('content_type_id', value)}
              error={validationErrors.content_type_id}
              required
              placeholder="Select a content type"
              disabled={loading}
            />

            {/* Tone */}
            <Dropdown
              label="Tone"
              value={formData.tone_new}
              options={toneOptions}
              onChange={(value) => handleInputChange('tone_new', value)}
              error={validationErrors.tone_new}
              required
              placeholder="Select a tone"
              disabled={loading}
            />

            {/* Desired Timeframe */}
            <Dropdown
              label="Desired Timeframe"
              value={formData.desired_timeframe}
              options={timeframeOptions}
              onChange={(value) => handleInputChange('desired_timeframe', value)}
              error={validationErrors.desired_timeframe}
              required
              placeholder="Select desired timeframe"
              disabled={loading}
            />

            {/* Target Market */}
            <Dropdown
              label="Target Market"
              value={formData.target_market}
              options={targetMarketOptions}
              onChange={(value) => handleInputChange('target_market', value)}
              error={validationErrors.target_market}
              required
              placeholder="Select target market"
              disabled={loading}
            />

            {/* Format */}
            <Dropdown
              label="Format"
              value={formData.format_type}
              options={formatOptions}
              onChange={(value) => handleInputChange('format_type', value)}
              error={validationErrors.format_type}
              required
              placeholder="Select format type"
              disabled={loading}
            />

            {/* Error Message */}
            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end space-x-4 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={loading}
              >
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-current border-t-transparent mr-2" />
                    Creating...
                  </div>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Create Project
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>

        {/* Help Text */}
        <div className={cn(fadeIn(400), "mt-6 text-center")}>
          <p className="text-sm text-muted-foreground">
            After creating your project, you'll be able to upload multiple videos and configure your compilation settings.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ProjectCreate; 